/**
 * Phase 261e — Academy "prominent sport" hook.
 *
 * Fetches `GET /api/academy/sport-anchor` ONCE per app session (module-level
 * cache) and returns the academy's configured primary sport plus the full
 * sport-config from `useSportConfig`. Used by the AppShell sidebar widget
 * and any other surface that needs the active workspace sport — without
 * fake AI/ML inference.
 *
 * Returns { sportKey, sportConfig, loading } where:
 *   sportKey    : 'football' | 'cricket' | 'basketball' | ... | null
 *   sportConfig : output of useSportConfig(sportKey)
 *   loading     : true while either fetch is in flight
 *
 * NOT applicable to students/parents — they get a null sportKey (caller
 * should hide its UI in that case).
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useSportConfig } from '@/hooks/useSportConfig';

let cachedAnchor = undefined;        // { prominent_sport, academy_name } or null
let inFlight = null;

async function fetchAnchorOnce() {
  if (cachedAnchor !== undefined) return cachedAnchor;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const r = await api.get('/academy/sport-anchor');
      cachedAnchor = r.data || null;
    } catch (_) {
      cachedAnchor = null;
    } finally {
      inFlight = null;
    }
    return cachedAnchor;
  })();
  return inFlight;
}

export function clearAcademySportCache() {
  cachedAnchor = undefined;
  inFlight = null;
}

const STAFF_ROLES = new Set(['academy_owner', 'coach', 'employee', 'super_admin']);

export function useAcademySport() {
  const { user } = useAuth();
  const role = user?.role;
  const isStaff = STAFF_ROLES.has(role);

  const [anchor, setAnchor] = useState(() => (cachedAnchor === undefined ? null : cachedAnchor));
  const [loadingAnchor, setLoadingAnchor] = useState(() => isStaff && cachedAnchor === undefined);

  useEffect(() => {
    if (!isStaff) {
      setAnchor(null);
      setLoadingAnchor(false);
      return;
    }
    if (cachedAnchor !== undefined) {
      setAnchor(cachedAnchor);
      setLoadingAnchor(false);
      return;
    }
    let cancelled = false;
    setLoadingAnchor(true);
    fetchAnchorOnce().then((v) => {
      if (!cancelled) {
        setAnchor(v);
        setLoadingAnchor(false);
      }
    });
    return () => { cancelled = true; };
  }, [isStaff]);

  const sportKey = (anchor?.prominent_sport || '').toLowerCase().trim() || null;
  const { config: sportConfig, loading: loadingConfig } = useSportConfig(sportKey || 'football');

  return {
    sportKey,
    academyName: anchor?.academy_name || '',
    sportConfig: sportKey ? sportConfig : null,
    loading: loadingAnchor || (sportKey ? loadingConfig : false),
  };
}
